import { clampRect } from '@/core/geometry'
import { drawSourceToImageData, isImageDataSource } from '@/core/image'
import { recognitionMetricKeys, type CaptureProfile, type LocatedMetricRegion, type NormalizedRect, type RecognitionMetricKey } from '@/domain/types'
import type { LayoutDetection, LayoutSearchOptions } from '../types'
import templates from './layout-templates.json'

interface Template { width: number; height: number; pixels: number[]; probe: number[][] }
export interface ArtaleAnchor { x: number; y: number; scale: number; error?: number }
const hudMetrics = ['level', 'jobName', 'hp', 'mp', 'exp'] as const
const labels = [templates.hp, templates.mp, templates.exp]
const offsets = [0, 165, 347]
const gray = (f: ImageData, x: number, y: number) => {
  const i = (Math.round(y) * f.width + Math.round(x)) * 4
  return (f.data[i]! + 2 * f.data[i + 1]! + f.data[i + 2]!) / 4
}
const rect = (f: ImageData, x: number, y: number, width: number, height: number): NormalizedRect =>
  clampRect({ x: x / f.width, y: y / f.height, width: width / f.width, height: height / f.height })

function error(f: ImageData, t: Template, x: number, y: number, scale: number, quick = false): number {
  if (x < 0 || y < 0 || x + t.width * scale > f.width || y + t.height * scale > f.height) return 255
  let sum = 0, n = 0
  for (const point of t.probe) {
    sum += Math.abs(gray(f, x + (point[0]! + .5) * scale - .5, y + (point[1]! + .5) * scale - .5) - point[2]!)
    if (++n >= 6 && sum / n > 85) return 255
  }
  if (sum / n > 65 || quick) return sum / n
  sum = 0; n = 0
  for (let yy = 0; yy < t.height; yy++) for (let xx = 0; xx < t.width; xx++) {
    sum += Math.abs(gray(f, x + (xx + .5) * scale - .5, y + (yy + .5) * scale - .5) - t.pixels[yy * t.width + xx]!)
    n++
  }
  return sum / n
}

function match(f: ImageData, t: Template, x: number, y: number, scale: number, tolerance = 42): boolean {
  for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++)
    if (error(f, t, x + dx, y + dy, scale) < tolerance) return true
  return false
}

function cancelled(options: LayoutSearchOptions) {
  if (options.signal?.aborted) throw new DOMException('Layout cancelled', 'AbortError')
  if (options.deadlineAt !== undefined && Date.now() >= options.deadlineAt) throw new DOMException('Layout timed out', 'TimeoutError')
}

/** Search border runs first; the three different labels and LV mark must agree. */
export function findArtaleHud(f: ImageData, options: LayoutSearchOptions = {}): ArtaleAnchor[] {
  const found: ArtaleAnchor[] = []
  for (let y = 0; y < f.height; y++) {
    if (!(y % 32)) cancelled(options)
    let start = -1
    for (let x = 0; x <= f.width; x++) {
      const bright = x < f.width && gray(f, x, y) > 205
      if (bright && start < 0) start = x
      if (bright || start < 0) continue
      const length = x - start, left = start; start = -1
      if (length < 78 || length > 650) continue
      const estimated = length / 161
      const scales = [...new Set([Math.round(estimated * 4) / 4, estimated])]
      for (const scale of scales) {
        if (scale < .5 || scale > 4) continue
        const a = { x: left - scale, y: y - 18 * scale, scale }
        if (found.some(b => Math.abs(b.x - a.x) < 8 * scale && Math.abs(b.y - a.y) < 8 * scale)) break
        if (!match(f, templates.hp, a.x, a.y, scale, 35)) continue
        if (!labels.slice(1).every((t, i) => match(f, t, a.x + offsets[i + 1]! * scale, a.y, scale, 52))) continue
        if (!match(f, templates.level, a.x - 328 * scale, a.y + 12 * scale, scale, 45)) continue
        found.push(a); break
      }
    }
  }
  return found
}

export function artaleHudRegions(f: ImageData, a: ArtaleAnchor): LayoutDetection {
  const { x, y, scale: s } = a
  const regions: LayoutDetection['regions'] = {}
  for (const [i, metric] of (['hp', 'mp', 'exp'] as const).entries()) {
    const px = x + offsets[i]! * s
    const area = rect(f, px, y, 164 * s, 40 * s)
    regions[metric] = { rect: area, textRect: rect(f, px, y, 164 * s, 18 * s),
      barRect: rect(f, px + (i === 1 ? 3 : 1) * s, y + 20 * s, (i === 1 ? 159 : 160) * s, 16 * s), confidence: .95 }
  }
  for (const [metric, dx, dy, w, h] of [
    ['level', -335, 7, 116, 34], ['jobName', -213, 0, 204, 18],
  ] as const) {
    const area = rect(f, x + dx * s, y + dy * s, w * s, h * s)
    regions[metric] = { rect: area, textRect: area, confidence: .95 }
  }
  return { hud: rect(f, x - 342 * s, y - 3 * s, 1280 * s, 48 * s), regions }
}

function findLabels(f: ImageData, template: Template, scales: readonly number[], options: LayoutSearchOptions): ArtaleAnchor[] {
  const found: ArtaleAnchor[] = []
  for (const scale of scales) for (let y = 0; y <= f.height - template.height * scale; y++) {
    if (!(y % 32)) cancelled(options)
    for (let x = 0; x <= f.width - template.width * scale; x++) {
      if (error(f, template, x, y, scale, true) > 48) continue
      let best: ArtaleAnchor | null = null, score = 38
      for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
        const value = error(f, template, x + dx, y + dy, scale)
        if (value < score) { best = { x: x + dx, y: y + dy, scale }; score = value }
      }
      if (best) {
        const previous = found.find(a => Math.abs(a.x - best!.x) < template.width * scale && Math.abs(a.y - best!.y) < template.height * scale)
        if (!previous) found.push({ ...best, error: score })
        else if (score < (previous.error ?? Infinity)) Object.assign(previous, best, { error: score })
      }
    }
  }
  return found
}

function anchorFromRegion(region: LocatedMetricRegion, f: { width: number; height: number }, metric: 'hp' | 'mp' | 'exp'): ArtaleAnchor {
  const s = region.rect.width * f.width / 164
  return { x: region.rect.x * f.width - offsets[['hp', 'mp', 'exp'].indexOf(metric)]! * s, y: region.rect.y * f.height, scale: s }
}

export function validateArtaleLayout(f: ImageData, detection: LayoutDetection): LayoutDetection {
  const regions = { ...detection.supportRegions, ...detection.regions }
  const bar = (['hp', 'mp', 'exp'] as const).find(m => regions[m])
  if (bar) {
    const a = anchorFromRegion(regions[bar]!, f, bar)
    const valid = labels.filter((t, i) => match(f, t, a.x + offsets[i]! * a.scale, a.y, a.scale, 52)).length >= 2
    for (const metric of hudMetrics) if (!valid) delete regions[metric]
  } else for (const metric of hudMetrics) delete regions[metric]
  const map = regions.mapName
  if (map) {
    const s = map.rect.height * f.height / 18
    if (!match(f, templates.minimap, map.rect.x * f.width - 43 * s, map.rect.y * f.height - 48 * s, s)) delete regions.mapName
  }
  if (regions.mesos && !artaleInventoryVisible(f, regions.mesos.rect)) delete regions.mesos
  return { ...detection, supportRegions: regions, regions: Object.fromEntries(Object.keys(detection.regions).filter(key => regions[key as RecognitionMetricKey]).map(key => [key, regions[key as RecognitionMetricKey]])) }
}

export function artaleInventoryVisible(f: ImageData, area: NormalizedRect): boolean {
  const s = area.height * f.height / 28, x = area.x * f.width, y = area.y * f.height
  return match(f, templates.coin, x - 26 * s, y + 4 * s, s)
    && match(f, templates.currency, x + 214 * s, y + 5 * s, s)
    && match(f, templates.inventory, x - 24 * s, y - 451 * s, s)
}

export function locateArtaleLayout(f: ImageData, metrics: readonly RecognitionMetricKey[] = recognitionMetricKeys, options: LayoutSearchOptions = {}): LayoutDetection {
  const start = performance.now(), regions: LayoutDetection['regions'] = {}
  let hud: NormalizedRect | undefined
  const anchors = metrics.some(m => (hudMetrics as readonly string[]).includes(m)) ? findArtaleHud(f, options) : []
  if (anchors.length > 1) return { regions: {}, diagnostics: { reason: 'multiple-scenes', candidates: anchors.length, durationMs: performance.now() - start } }
  if (anchors[0]) {
    const detected = artaleHudRegions(f, anchors[0]); hud = detected.hud
    for (const metric of hudMetrics) if (metrics.includes(metric)) regions[metric] = detected.regions[metric]
  }
  const hintBar = options.hint?.regions.hp ?? options.hint?.regions.mp ?? options.hint?.regions.exp
  const scales = anchors[0] ? [anchors[0].scale] : hintBar ? [hintBar.rect.width * f.width / 164] : [1, 1.5]
  if (metrics.includes('mapName')) {
    const maps = findLabels(f, templates.minimap, scales, options)
    if (maps.length === 1) {
      const a = maps[0]!, area = rect(f, a.x + 43 * a.scale, a.y + 48 * a.scale, 196 * a.scale, 18 * a.scale)
      regions.mapName = { rect: area, textRect: area, confidence: .95 }
    }
  }
  if (metrics.includes('mesos')) {
    if (options.mesosCandidate) {
      if (artaleInventoryVisible(f, options.mesosCandidate.rect)) regions.mesos = options.mesosCandidate
    } else {
      try {
        const candidates = findLabels(f, templates.inventory, scales, { ...options, deadlineAt: Math.min(options.deadlineAt ?? Infinity, Date.now() + (options.mesosBudgetMs ?? 2_000)) })
        .map(a => ({ rect: rect(f, a.x + 24 * a.scale, a.y + 451 * a.scale, 208 * a.scale, 28 * a.scale), confidence: .95 }))
        .filter(region => artaleInventoryVisible(f, region.rect))
        if (candidates.length === 1) regions.mesos = { ...candidates[0]!, textRect: candidates[0]!.rect }
      } catch (error) {
        if (!(error instanceof DOMException) || error.name !== 'TimeoutError' || options.signal?.aborted) throw error
        return { hud, regions, diagnostics: { candidates: anchors.length, durationMs: performance.now() - start, reasons: { mesos: 'timeout' } } }
      }
    }
  }
  return { hud, regions, diagnostics: { candidates: anchors.length, anchors: anchors.length ? 4 : regions.mesos ? 3 : regions.mapName ? 2 : 0, durationMs: performance.now() - start, scaleX: anchors[0]?.scale, scaleY: anchors[0]?.scale } }
}

export function validArtaleBar(source: CanvasImageSource | ImageData, width: number, height: number, metric: 'hp' | 'mp' | 'exp', region: LocatedMetricRegion): boolean {
  const f = isImageDataSource(source) ? source : drawSourceToImageData(source, width, height, region.rect)
  const s = region.rect.width * width / 164
  return match(f, labels[['hp', 'mp', 'exp'].indexOf(metric)]!, isImageDataSource(source) ? region.rect.x * width : 0, isImageDataSource(source) ? region.rect.y * height : 0, s, 52)
}

export function locatedArtaleJobRect(profile: CaptureProfile, width: number, height: number): NormalizedRect | undefined {
  const metric = (['hp', 'mp', 'exp'] as const).find(m => profile.autoRois?.[m])
  if (!metric) return profile.autoRois?.jobName?.rect
  const a = anchorFromRegion(profile.autoRois![metric]!, { width, height }, metric)
  return { x: (a.x - 213 * a.scale) / width, y: a.y / height, width: 204 * a.scale / width, height: 18 * a.scale / height }
}
